import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '@/services/firebase';
import Button from '@/components/ui/Button';
import { ArrowLeftIcon, PrinterIcon } from '@heroicons/react/24/outline';

interface WireTransfer {
  id: string;
  fromAccount: string;
  toAccount: string;
  toRoutingNumber: string;
  bankCode: string;
  requestedBy: string;
  amount: number;
  currency: string;
  senderLocation: 'in-person' | 'remote';
  isManagerConfirmed: boolean;
  isRecipientConfirmed: boolean;
  isSenderConfirmed: boolean;
  isSent: boolean;
  initiatedDate: Date;
}

interface TrustedSender {
  id: string;
  name: string;
  urImage: string; // base64 image data
}

export default function WireSummaryPage() {
  const { wireId } = useParams<{ wireId: string }>();
  const navigate = useNavigate();
  const [wire, setWire] = useState<WireTransfer | null>(null);
  const [loading, setLoading] = useState(true);
  const [trustedSenders, setTrustedSenders] = useState<TrustedSender[]>([]);

  const fetchTrustedSenders = async (fromAccount: string) => {
    try {
      console.log('Fetching trusted senders for account:', fromAccount);
      
      // Query the accounts collection for documents with matching accountNumber
      const accountsRef = collection(db, 'accounts');
      const q = query(accountsRef, where('accountNumber', '==', fromAccount));
      const querySnapshot = await getDocs(q);
      
      const senders: TrustedSender[] = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        console.log('Found account document:', doc.id, data);
        
        // Check if this document has trustedSenders records (note: plural)
        if (data.trustedSenders && Array.isArray(data.trustedSenders)) {
          data.trustedSenders.forEach((sender: any, index: number) => {
            if (sender.name && sender.urImage) {
              senders.push({
                id: `${doc.id}_${index}`,
                name: sender.name,
                urImage: sender.urImage
              });
            }
          });
        }
      });
      
      console.log('Found trusted senders:', senders);
      setTrustedSenders(senders);
    } catch (error) {
      console.error('Error fetching trusted senders:', error);
      setTrustedSenders([]);
    }
  };

  useEffect(() => {
    const fetchWire = async () => {
      if (!wireId) {
        setLoading(false);
        return;
      }

      try {
        const wireDoc = await getDoc(doc(db, 'wires', wireId));
        if (wireDoc.exists()) {
          const data = wireDoc.data();
          const wireData = {
            id: wireDoc.id,
            fromAccount: data.fromAccount,
            toAccount: data.toAccount,
            toRoutingNumber: data.toRoutingNumber,
            bankCode: data.bankCode,
            requestedBy: data.requestedBy,
            amount: data.amount,
            currency: data.currency,
            senderLocation: data.senderLocation || 'in-person',
            isManagerConfirmed: data.isManagerConfirmed || false,
            isRecipientConfirmed: data.isRecipientConfirmed || false,
            isSenderConfirmed: data.isSenderConfirmed || false,
            isSent: data.isSent || false,
            initiatedDate: data.initiatedDate?.toDate() || new Date(),
          };
          setWire(wireData);
          
          // Fetch trusted senders for this account
          await fetchTrustedSenders(wireData.fromAccount);
        }
      } catch (error) {
        console.error('Error fetching wire transfer:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchWire();
  }, [wireId]);

  const handlePrint = () => {
    window.print();
  };

  const handleExit = () => {
    navigate('/wire-transfers');
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="loading-spinner mx-auto mb-4" />
          <p className="text-gray-600">Loading wire transfer summary...</p>
        </div>
      </div>
    );
  }

  if (!wire) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Wire Transfer Not Found</h2>
          <Button onClick={handleExit}>Back to Wire Transfers</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Action Buttons - Hidden when printing */}
      <div className="print:hidden bg-white shadow-sm border-b border-gray-200">
        <div className="container-centered py-4">
          <div className="flex items-center justify-between">
            <Button variant="ghost" size="sm" onClick={handleExit}>
              <ArrowLeftIcon className="h-4 w-4 mr-2" />
              Exit
            </Button>
            <Button onClick={handlePrint} className="bg-blue-600 hover:bg-blue-700 text-white">
              <PrinterIcon className="h-4 w-4 mr-2" />
              Print
            </Button>
          </div>
        </div>
      </div>

      {/* Wire Transfer Summary Document */}
      <div className="container-centered py-8 print:py-0">
        <div className="max-w-4xl mx-auto bg-white shadow-lg print:shadow-none">
          {/* Document Header */}
          <div className="border-b-2 border-gray-900 p-8 print:p-6">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Wire Transfer Summary</h1>
            <p className="text-gray-600">Document ID: {wire.id}</p>
            <p className="text-gray-600">Generated: {new Date().toLocaleString()}</p>
          </div>

          {/* Wire Transfer Details */}
          <div className="p-8 print:p-6">
            {/* Amount Section */}
            <div className="mb-8 pb-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-700 mb-4">Transfer Amount</h2>
              <div className="text-4xl font-bold text-gray-900">
                {formatCurrency(wire.amount, wire.currency)}
              </div>
              <div className="text-sm text-gray-600 mt-1">{wire.currency}</div>
            </div>

            {/* Sender Information */}
            <div className="mb-8 pb-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-700 mb-4">Sender Information</h2>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <div className="text-sm font-medium text-gray-500 mb-1">Requested By</div>
                  <div className="text-base text-gray-900">{wire.requestedBy}</div>
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-500 mb-1">Sender Location</div>
                  <div className="text-base text-gray-900">
                    {wire.senderLocation === 'in-person' ? 'In-Person' : 'Remote'}
                  </div>
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-500 mb-1">From Account</div>
                  <div className="text-base font-mono text-gray-900">{wire.fromAccount}</div>
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-500 mb-1">Bank Code</div>
                  <div className="text-base text-gray-900">{wire.bankCode}</div>
                </div>
              </div>
              
              {/* QR Code Section - Only show if trusted senders exist */}
              {trustedSenders.length > 0 && (
                <div className="mt-6">
                  <div className="text-sm font-medium text-gray-500 mb-2">
                    {trustedSenders.length === 1 ? trustedSenders[0].name : 'Trusted Senders'}
                  </div>
                  <div className={`${trustedSenders.length > 1 ? 'flex flex-wrap gap-4' : 'inline-block'} border-2 border-dashed border-gray-400 bg-gray-50 p-4`}>
                    {trustedSenders.map((sender) => (
                      <div key={sender.id} className="text-center">
                        <div 
                          className="flex items-center justify-center bg-white border border-gray-300 mx-auto"
                          style={{ width: '1.25in', height: '1.25in' }}
                        >
                          {sender.urImage ? (
                            <img 
                              src={sender.urImage.startsWith('data:') ? sender.urImage : `data:image/png;base64,${sender.urImage}`} 
                              alt={`QR Code for ${sender.name}`}
                              className="w-full h-full object-contain"
                              onError={(e) => {
                                console.error(`Failed to load QR code image for ${sender.name}:`, sender.urImage.substring(0, 100) + '...');
                                // Hide the broken image and show fallback
                                e.currentTarget.style.display = 'none';
                              }}
                            />
                          ) : (
                            <div className="text-center">
                              <div className="text-gray-400 text-xs mb-1">
                                <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                                </svg>
                              </div>
                              <div className="text-xs text-gray-500">QR Code</div>
                              <div className="text-xs text-gray-400">1.25" × 1.25"</div>
                            </div>
                          )}
                        </div>
                        {trustedSenders.length > 1 && (
                          <div className="text-xs text-gray-600 mt-2">{sender.name}</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Recipient Information */}
            <div className="mb-8 pb-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-700 mb-4">Recipient Information</h2>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <div className="text-sm font-medium text-gray-500 mb-1">To Account</div>
                  <div className="text-base font-mono text-gray-900">{wire.toAccount}</div>
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-500 mb-1">Routing Number</div>
                  <div className="text-base font-mono text-gray-900">{wire.toRoutingNumber}</div>
                </div>
              </div>
            </div>

            {/* Status Information */}
            <div className="mb-8">
              <h2 className="text-lg font-semibold text-gray-700 mb-4">Transfer Status</h2>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center">
                  <div className={`w-4 h-4 rounded-full mr-3 ${wire.isManagerConfirmed ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                  <span className="text-sm text-gray-700">Manager Confirmed</span>
                </div>
                <div className="flex items-center">
                  <div className={`w-4 h-4 rounded-full mr-3 ${wire.isRecipientConfirmed ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                  <span className="text-sm text-gray-700">Recipient Confirmed</span>
                </div>
                <div className="flex items-center">
                  <div className={`w-4 h-4 rounded-full mr-3 ${wire.isSenderConfirmed ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                  <span className="text-sm text-gray-700">Sender Confirmed</span>
                </div>
                <div className="flex items-center">
                  <div className={`w-4 h-4 rounded-full mr-3 ${wire.isSent ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                  <span className="text-sm text-gray-700">Sent</span>
                </div>
              </div>
              <div className="mt-4 text-sm text-gray-600">
                Initiated: {wire.initiatedDate.toLocaleString()}
              </div>
            </div>

            {/* Footer */}
            <div className="pt-6 border-t border-gray-200 text-center text-sm text-gray-500">
              <p>This is an official wire transfer summary document.</p>
              <p className="mt-1">Please retain this document for your records.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

